/**
 * SB2-CC-01 — structured 403 → toast copy.
 *
 * Maps an Axios-style error (or the `AeroApiError` wrapper produced by
 * `@aeriosiq/api-client`) carrying the `ApiError403Structured`
 * payload into a one-line toast copy. The structured envelope is
 *
 *   { error: "forbidden", reason, your_role, your_tenant, resource_tenant }
 *
 * Per AC11 the FE renders `reason` verbatim — NOT the generic
 * "something went wrong" copy. Reason-specific copy:
 *
 *   - `permission_denied`        — "Your role doesn't permit this action."
 *   - `tenant_scope_violation`   — "This resource belongs to another tenant."
 *   - `conditional_predicate_failed` —
 *       "Your role permits this only conditionally; this case isn't allowed."
 *   - `role_escalation_forbidden` — "You can't promote a user to that role."
 *   - `self_promotion_forbidden` — "You can't change your own role here."
 *   - `wrong_password`           — "That password didn't match."
 *
 * Falls back to a generic copy when the body is missing or
 * non-structured (e.g. legacy 4.x 403s from endpoints not yet bumped
 * to 5.0.0). Returns `null` for non-403 errors so the caller knows to
 * pick its own fallback copy.
 */

export type Structured403Reason =
  | "role_escalation_forbidden"
  | "self_promotion_forbidden"
  | "wrong_password"
  | "permission_denied"
  | "tenant_scope_violation"
  | "conditional_predicate_failed";

export interface Structured403Body {
  error: "forbidden";
  reason: Structured403Reason;
  your_role: string;
  your_tenant?: string | null;
  resource_tenant?: string | null;
}

const REASON_COPY: Readonly<Record<Structured403Reason, string>> = {
  permission_denied: "Your role doesn't permit this action.",
  tenant_scope_violation: "This resource belongs to another tenant.",
  conditional_predicate_failed:
    "Your role permits this only conditionally; this case isn't allowed.",
  role_escalation_forbidden: "You can't promote a user to that role.",
  self_promotion_forbidden: "You can't change your own role here.",
  wrong_password: "That password didn't match.",
};

interface AxiosLikeError {
  response?: {
    status?: number;
    data?: unknown;
  };
  status?: number;
  cause?: { response?: { status?: number; data?: unknown } };
}

/**
 * Extract the structured 403 body from an unknown error, accepting
 * both the raw Axios shape (`err.response.data`) and the wrapped
 * `AeroApiError` shape (`err.cause.response.data`).
 */
function extractStructured403Body(err: unknown): Structured403Body | null {
  if (typeof err !== "object" || err === null) return null;
  const e = err as AxiosLikeError;
  const status = e.response?.status ?? e.status ?? e.cause?.response?.status;
  if (status !== 403) return null;
  const data = e.response?.data ?? e.cause?.response?.data;
  if (typeof data !== "object" || data === null) return null;
  const candidate = data as Partial<Structured403Body>;
  if (candidate.error !== "forbidden") return null;
  if (typeof candidate.reason !== "string") return null;
  if (typeof candidate.your_role !== "string") return null;
  // We have enough fields to call it a structured envelope.
  return candidate as Structured403Body;
}

/**
 * Public API. Returns toast copy when the error is a structured 403,
 * `null` otherwise. Pair with the caller's own generic fallback:
 *
 *   const copy = errorToastFor403(err) ?? "Couldn't save site. Try again.";
 *   toast(copy, "error");
 */
export function errorToastFor403(err: unknown): string | null {
  const body = extractStructured403Body(err);
  if (!body) return null;
  return REASON_COPY[body.reason];
}
